import { request } from "$/lib/api"
import { Review, Route, populateRoute } from "@repo/common"
import { ReviewStats, ReviewStatsFilters } from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface ReviewFilters {
  storeId: string
}

async function fetchReviews(filters: ReviewFilters): Promise<Review[]> {
  return await request<Review[]>({
    url: populateRoute(
      Route.CUSTOMER_REVIEW__FIND_ALL_BY_STORE,
      filters.storeId,
    ),
    method: "GET",
  })
}

async function fetchReviewStats(
  filters: ReviewStatsFilters,
): Promise<ReviewStats> {
  return await request<ReviewStats>({
    url: populateRoute(Route.CUSTOMER_REVIEW__STATS, filters.storeId),
    method: "GET",
    params: {
      period: filters.period,
    },
  })
}

const reviewQueries = {
  all: () => ["reviews"],
  lists: () => [...reviewQueries.all(), "list"],
  list: (filters: ReviewFilters) =>
    queryOptions({
      queryKey: [...reviewQueries.lists(), filters],
      queryFn: () => fetchReviews(filters),
    }),
  stats: () => [...reviewQueries.all(), "stats"],
  statsForPeriod: (filters: ReviewStatsFilters) =>
    queryOptions({
      queryKey: [...reviewQueries.stats(), filters],
      queryFn: () => fetchReviewStats(filters),
    }),
}

async function respondToReview(
  jobId: string,
  storeResponse: string,
): Promise<Review> {
  return await request<Review>({
    url: populateRoute(Route.CUSTOMER_REVIEW__RESPOND, jobId),
    method: "POST",
    data: { storeResponse },
  })
}

export const useRespondToReviewMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      jobId,
      storeResponse,
    }: {
      jobId: string
      storeResponse: string
    }) => respondToReview(jobId, storeResponse),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: reviewQueries.all() })
    },
  })
}

export default reviewQueries
